// Review tasks a browser sends an agent (docs/remote-relay.md, Review tasks):
// their two kinds, what each payload holds, and what each needs granted.
// The plugin's plugins/changeling/scripts/task.mjs checks the same, and
// refuses any other shape.
import { matchKey, type RelayCategory, type SubjectKey } from './relaySubject';
import { sha256Hex } from '../logic/sha256';

export const RANGE_TASK = 'changeling.review-range.v1';
export const SUBJECT_TASK = 'changeling.review-subject.v1';
export type TaskKind = typeof RANGE_TASK | typeof SUBJECT_TASK;

/** One earlier turn of a follow-up: the person's request, or the agent's answer. */
export interface Turn { from: 'person' | 'agent'; text: string }
/** A pull request review thread a range task is on, with its comments quoted. */
export interface TaskThread { id: string; comments: { author: string | null; body: string }[] }
/**
 * Lines of one file as the person saw them: `side` `old` (before the
 * change) or `new` (after it), `blob` git's object ID of that side's file
 * (`git rev-parse <commit>:<path>`), `start_line`..`end_line` in it (1-based,
 * inclusive), read at `commit`; `head` the subject's head the person was
 * looking at.
 */
export interface RangePayload {
  subject: SubjectKey;
  head: string;
  path: string;
  side: 'old' | 'new';
  blob: string;
  start_line: number;
  end_line: number;
  commit: string;
  thread?: TaskThread;
  instruction: string;
  conversation?: Turn[];
}
/** A question about the whole subject; `quote`, a passage of its description asked about. */
export interface SubjectPayload {
  subject: SubjectKey;
  head: string;
  instruction: string;
  quote?: string;
  conversation?: Turn[];
}
/** A task as it is sent: its kind and the payload of that kind. */
export type TaskBody = { kind: typeof RANGE_TASK; payload: RangePayload } | { kind: typeof SUBJECT_TASK; payload: SubjectPayload };

/** What a review thread's task carries: its comments, and the file and lines it is on. */
export const THREAD_NEEDS: readonly RelayCategory[] = ['comments', 'files'];
/** What a range's task carries without a thread: the file and lines. */
export const RANGE_NEEDS: readonly RelayCategory[] = ['files'];
/** What a question about the subject carries: its description and state (a quoted passage of it). */
export const QUESTION_NEEDS: readonly RelayCategory[] = ['metadata'];
/** What a task carries, so what its grant must cover. */
export function taskScope(body: TaskBody): readonly RelayCategory[] {
  return body.kind === RANGE_TASK ? (body.payload.thread ? THREAD_NEEDS : RANGE_NEEDS) : QUESTION_NEEDS;
}
/** The categories of `needs` that `scope` lacks. */
export const lacking = (needs: readonly RelayCategory[], scope: readonly RelayCategory[]) => needs.filter((c) => !scope.includes(c));

// Topics: what a task is about in this browser, under which the helper
// allows one active task and the panel finds its tasks (relay.ts `tasks`,
// `sent`). Each names its subject by a digest of its match key (`matchKey`:
// owner and repo lowercased), the first 128 bits of its SHA-256 in hex, so
// subjects whose numbers or branch names agree (o/r#7 and x/y#7) never
// share one, and a topic is short and plain whatever the key holds (a long
// compare, a branch with a Unicode space). The readable subject travels in
// the task's payload. The wire contract's `topics` holds the plugin's
// task.mjs `validTopic` to these.
const digest = (subject: SubjectKey) => sha256Hex(new TextEncoder().encode(matchKey(subject))).slice(0, 32);
/** The topic of the questions about `subject` as a whole: its conversation. */
export const subjectTopic = (subject: SubjectKey) => `conversation ${digest(subject)}`;
/** The topic of review thread `thread` (a GitHub node ID, or a draft's) of `subject`. */
export const threadTopic = (subject: SubjectKey, thread: string) => `thread ${digest(subject)} ${thread}`;
/** A range of lines' ID in a topic: 24 lowercase hex digits (logic/rangeAsk.ts `rangeTopic` makes one from the lines). */
export const LINES_ID = /^[0-9a-f]{24}$/;
/** The topic of lines `id` of `subject` asked about with no thread (a compare's, from its Files view). */
export function linesTopic(subject: SubjectKey, id: string): string {
  if (!LINES_ID.test(id)) throw new Error('A lines topic takes 24 lowercase hex digits.');
  return `lines ${digest(subject)} ${id}`;
}
/** Whether `t` is a topic as these write one (a record kept before topics named their subject is not). */
export const isTopic = (t: unknown): t is string => typeof t === 'string' && /^(?:conversation [0-9a-f]{32}|thread [0-9a-f]{32} [\x21-\x7e]{1,200}|lines [0-9a-f]{32} [0-9a-f]{24})$/.test(t);
